import { useId, useState } from 'react';
import { cva } from 'class-variance-authority';
import { clsx } from 'clsx';
import { Button, Card, Flex, Skeleton, Tag, Text } from '@nula/core-ui';
import { useMarkdownPreview } from '@/lib/documents/markdown-preview-context';
import type { ProposalSection } from '@/lib/templates/api';
import {
  CONFLICT_SENTENCE,
  REVIEW_LABELS,
  UNPLACEABLE_SENTENCE,
} from '@/lib/templates/review-copy';
import type { TextReview } from '../hooks/use-text-review';
import { sectionProgress } from '../utils/review-progress';
import type { ReviewState } from '../utils/review-decisions';
import styles from './surface-review-card.module.css';

const card = cva(styles.card, {
  variants: {
    tone: { open: styles.open, conflicted: styles.conflicted },
  },
});

const DIFF_LABELS = {
  added: REVIEW_LABELS.diffAdded,
  removed: REVIEW_LABELS.diffRemoved,
  formattingChanged: REVIEW_LABELS.formattingChanged,
};

interface SurfaceReviewCardProps {
  section: ProposalSection;
  state: ReviewState;
  review: TextReview;
}

function chipLabel(section: ProposalSection): string {
  if (section.current === '') return REVIEW_LABELS.chipNewSection;
  if (section.proposed === '') return REVIEW_LABELS.chipRemoved;
  return REVIEW_LABELS.chipChanged;
}

function SectionBody({ section }: { section: ProposalSection }) {
  const Preview = useMarkdownPreview();
  const paneId = useId();
  const [showCurrent, setShowCurrent] = useState(false);
  if (!Preview) return <Skeleton />;

  if (section.proposed === '') {
    return (
      <Flex vertical className={styles.pane}>
        <Text type="secondary" size="sm">{REVIEW_LABELS.paneCurrent}</Text>
        <Preview markdown={section.current} />
      </Flex>
    );
  }

  const diff = <Preview markdown={section.proposed} baseline={section.current} labels={DIFF_LABELS} />;
  if (section.current === '') return diff;

  return (
    <Flex vertical className={styles.stack}>
      <Flex>
        <Button
          size="sm"
          variant="text"
          className={styles.touch}
          aria-expanded={showCurrent}
          aria-controls={paneId}
          onClick={() => setShowCurrent((open) => !open)}
        >
          {showCurrent ? REVIEW_LABELS.hideCurrent : REVIEW_LABELS.showCurrent}
        </Button>
      </Flex>
      {showCurrent ? (
        <Flex vertical id={paneId} className={styles.stack}>
          <Flex vertical className={clsx(styles.pane, styles.currentPane)}>
            <Text type="secondary" size="sm">{REVIEW_LABELS.paneCurrent}</Text>
            <Preview markdown={section.current} />
          </Flex>
          <Flex vertical className={styles.pane}>
            <Text type="secondary" size="sm">{REVIEW_LABELS.paneNew}</Text>
            {diff}
          </Flex>
        </Flex>
      ) : (
        <Flex vertical id={paneId} className={styles.stack}>{diff}</Flex>
      )}
    </Flex>
  );
}

function FoldedRow({ heading, label, color, disabled, onUndo }: {
  heading: string; label: string; color: 'success' | 'default'; disabled: boolean; onUndo?: () => void;
}) {
  return (
    <Flex align="center" className={styles.row}>
      <Text type="secondary" size="sm">{heading}</Text>
      <Flex className={styles.spacer} />
      <Tag color={color} bordered={false}>{label}</Tag>
      {onUndo ? (
        <Button size="sm" variant="text" className={styles.touch} disabled={disabled} onClick={onUndo}>
          {REVIEW_LABELS.undo}
        </Button>
      ) : null}
    </Flex>
  );
}

export function SurfaceReviewCard({ section, state, review }: SurfaceReviewCardProps) {
  const { key, heading } = section;
  const disabled = review.busy;
  const progress = sectionProgress(state, section);
  const conflictRound = state.round === 'conflicts' && state.outcomes[key] !== undefined;

  if (progress === 'unchanged') {
    return <FoldedRow heading={heading} label={REVIEW_LABELS.noChange} color="default" disabled={disabled} />;
  }

  if (progress === 'decided') {
    if (conflictRound) {
      const outcome = state.outcomes[key];
      if (outcome === 'applied' || outcome === 'kept') {
        return (
          <FoldedRow
            heading={heading}
            label={outcome === 'applied' ? REVIEW_LABELS.applied : REVIEW_LABELS.kept}
            color={outcome === 'applied' ? 'success' : 'default'}
            disabled={disabled}
          />
        );
      }
      const forced = state.conflictChoices[key] === 'force';
      return (
        <FoldedRow
          heading={heading}
          label={forced ? REVIEW_LABELS.usingNew : REVIEW_LABELS.keepingCurrent}
          color={forced ? 'success' : 'default'}
          disabled={disabled}
          onUndo={() => review.chooseConflict(key, null)}
        />
      );
    }
    const used = state.choices[key] === 'use';
    return (
      <FoldedRow
        heading={heading}
        label={used ? REVIEW_LABELS.usingNew : REVIEW_LABELS.keepingCurrent}
        color={used ? 'success' : 'default'}
        disabled={disabled}
        onUndo={() => review.choose(key, null)}
      />
    );
  }

  const conflicted = conflictRound;
  const unforceable = conflicted && state.unforceable[key] === true;
  const decide = (choice: 'keep' | 'use') =>
    conflicted ? review.chooseConflict(key, choice === 'use' ? 'force' : 'keep') : review.choose(key, choice);

  return (
    <Card bordered={false} size="small" className={card({ tone: conflicted ? 'conflicted' : 'open' })}>
      <Flex vertical className={styles.stack}>
        <Flex align="center" className={styles.header}>
          <Text strong>{heading}</Text>
          <Tag color="default" bordered={false}>{chipLabel(section)}</Tag>
          <Flex className={styles.spacer} />
          <Button
            size="sm"
            variant="default"
            className={styles.touch}
            aria-keyshortcuts="C"
            disabled={disabled}
            onClick={() => decide('keep')}
          >
            {REVIEW_LABELS.keepCurrent}
          </Button>
          {unforceable ? null : (
            <Button
              size="sm"
              variant="primary"
              className={styles.touch}
              aria-keyshortcuts="U"
              disabled={disabled}
              onClick={() => decide('use')}
            >
              {conflicted ? REVIEW_LABELS.useNewAnyway : REVIEW_LABELS.useNew}
            </Button>
          )}
        </Flex>
        {conflicted ? (
          <Text size="sm" className={styles.conflictText}>
            {unforceable ? UNPLACEABLE_SENTENCE : CONFLICT_SENTENCE}
          </Text>
        ) : null}
        <SectionBody section={section} />
      </Flex>
    </Card>
  );
}
